import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import CreateContactModal from "@/components/layouts/CreateContactModal";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import { useResetPartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import { PartnerData } from "@/pages/partners/types";
import { UserData } from "@/entities/users/types";

export const usePartnerActions = (
  partnerEntity: PartnerData | undefined,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleCopyLink = useCopyLink();
  const resetPartnerContacts = useResetPartnerContactsInfinite();

  const {
    partner,
    resetPartners,
    onUpdatePartnerField: onUpdateField,
    deletePartner
  } = useEntityType(
    EntityType.PARTNER,
    partnerEntity,
    false,
    undefined,
    undefined,
    forceRefetch,
    includeAdditionals
  );

  const [isOpenDeletePartnerModal, setIsOpenDeletePartnerModal] =
    useState(false);
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false);
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);

  const handleDelete = (
    setIsOpen: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (partner) {
      deletePartner.mutate(
        {
          pathParams: { partnerId: partner.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpen(false);
              closeDrawer?.();
            } else {
              navigate("/partners", { replace: true });
            }
            resetPartners();
            toast.success(
              <ToastBody
                title={`${t("partners.partnerView.toast1")} ${partner?.name || ""} ${t("partners.partnerView.toast2")}`}
              />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const getCreateOptions = () => {
    return {
      lead: () => setIsCreateLeadModalOpen(true),
      deal: () => setIsCreateDealModalOpen(true),
      person: {
        action: () => setIsCreateContactModalOpen(true),
        customText: t("detailedView.create.partnerPerson")
      }
    } as DetailedViewOptions;
  };

  const getOptions = (isQuickView: boolean = false, partnerId?: string) => {
    return {
      copyLink: () => handleCopyLink(isQuickView, partnerId),
      partnerLog: () =>
        isQuickView
          ? navigate(`/partners/${partnerId}/log`, { replace: true })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeletePartnerModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const renderModals = (
    isDropdownClosing = false,
    isQuickView = false,
    onClose?: () => void,
    userData?: UserData
  ) => {
    return (
      <>
        <DeleteEntityModal
          isOpen={isOpenDeletePartnerModal && !isDropdownClosing}
          setIsOpen={setIsOpenDeletePartnerModal}
          entity={EntityType.PARTNER}
          entityName={partner?.name || ""}
          handleDelete={() =>
            handleDelete(setIsOpenDeletePartnerModal, isQuickView, onClose)
          }
        />
        {userData && (
          <>
            <CreateLeadModal
              isOpen={isCreateLeadModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreateLeadModalOpen}
              userData={userData}
              partnerId={partner?.id}
            />
            <CreateDealModal
              isOpen={isCreateDealModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreateDealModalOpen}
              userData={userData}
              partnerId={partner?.id}
            />
          </>
        )}
        <CreateContactModal
          isOpen={isCreateContactModalOpen && !isDropdownClosing}
          setIsOpen={setIsCreateContactModalOpen}
          resetPage={resetPartnerContacts}
          partnerId={partner?.id}
        />
      </>
    );
  };

  return {
    partner,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getCreateOptions,
    getOptions,
    renderModals
  };
};
